/**
 * Error Boundary
 *
 * Catches React render errors and displays a recovery UI
 * instead of crashing the entire application.
 */

import { Component, type ReactNode } from 'react';
import { colors } from '@/theme';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="min-h-[400px] flex items-center justify-center p-8"
          style={{ background: colors.obsidian }}
        >
          <div
            className="max-w-md w-full p-8 rounded-xl text-center"
            style={{ background: colors.ironCharcoal, border: `1px solid ${colors.forgeSteel}` }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: `${colors.emberOrange}20` }}
            >
              <AlertTriangle size={32} style={{ color: colors.emberOrange }} />
            </div>
            <h2
              className="text-xl font-semibold mb-2"
              style={{ color: colors.sandstone, fontFamily: 'Sora, sans-serif' }}
            >
              Something went wrong
            </h2>
            <p className="text-sm mb-6" style={{ color: colors.secondaryText }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all hover:scale-105"
              style={{ background: colors.foundryCopper, color: colors.obsidian }}
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
